import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInterestList } from '../context/InterestListContext';
import { getProducts } from '../products';
import { API_ENDPOINTS } from '../config/api';
import './AdvancedQuote.css';
import { ArrowLeft, Plus, Minus, Heart, Calculator } from 'phosphor-react';

const AdvancedQuote = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addToInterestList, interestList } = useInterestList();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeCategory, setActiveCategory] = useState('todos');
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        const productsData = await getProducts();
        setProducts(productsData);
        
        // Se veio da lista de interesse, pré-selecionar os produtos
        const fromInterestList = searchParams.get('from') === 'interest-list';
        if (fromInterestList && Object.keys(interestList).length > 0) {
          const preSelected = {};
          Object.entries(interestList).forEach(([productId, quantity]) => {
            if (quantity > 0) {
              preSelected[productId] = quantity;
            }
          });
          setSelectedProducts(preSelected);
        }
      } catch (error) {
        console.error('Erro ao carregar produtos:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [searchParams, interestList]);

  // Preencher formulário com dados do utilizador logado
  useEffect(() => {
    const username = localStorage.getItem('username');
    const email = localStorage.getItem('email');
    const phone = localStorage.getItem('phone');
    const token = localStorage.getItem('token');
    
    if (username && email && token) {
      setCustomerInfo(prev => ({
        ...prev,
        name: username,
        email: email,
        phone: phone || ''
      }));
    }
  }, []);

  const categories = [
    { id: 'todos', name: 'Todos os Produtos' },
    { id: 'Videovigilância', name: 'Videovigilância' },
    { id: 'Alarmes', name: 'Alarmes' },
    { id: 'Videoporteiros', name: 'Videoporteiro' },
    { id: 'Acessórios', name: 'Acessórios' }
  ];

  const handleProductQuantityChange = (productId, quantity) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: Math.max(0, quantity)
    }));
  };

  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getSelectedProductsCount = () => {
    return Object.values(selectedProducts).reduce((sum, qty) => sum + qty, 0);
  };

  const filteredProducts = activeCategory === 'todos' 
    ? products 
    : products.filter(product => product.category === activeCategory);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(API_ENDPOINTS.ADVANCED_QUOTE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerInfo,
          selectedProducts,
          notes: customerInfo.notes
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'ok') {
        setSubmitStatus({
          type: 'success',
          message: data.message
        });
        // Navegar para página de obrigado após 2 segundos
        setTimeout(() => {
          navigate('/orcamento/obrigado');
        }, 2000);
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.error || 'Erro ao enviar orçamento. Tente novamente.'
        });
      }
    } catch (error) {
      console.error('Erro ao enviar orçamento:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Erro de conexão. Verifique se o servidor está rodando.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addAllToInterestList = () => {
    Object.entries(selectedProducts).forEach(([productId, quantity]) => {
      if (quantity > 0) {
        addToInterestList(parseInt(productId), quantity);
      }
    });
    navigate('/lista-interesse');
  };

  return (
    <div className="advanced-quote-container">
      <div className="advanced-quote-content">
        <div className="advanced-quote-header">
          <button 
            className="back-button" 
            onClick={() => navigate('/orcamento')}
          >
            <ArrowLeft size={20} />
            Voltar
          </button>
          <h1>Orçamento Avançado</h1>
          <p>Selecione os produtos que deseja incluir no seu orçamento</p>
          {searchParams.get('from') === 'interest-list' && (
            <div className="interest-import-notice">
              💝 Produtos da sua Lista de Interesse foram carregados automaticamente
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading-container">
            <p>Carregando produtos...</p>
          </div>
        ) : (
          <>
            <div className="quote-summary">
          <div className="summary-item">
            <span>Produtos selecionados: {getSelectedProductsCount()}</span>
          </div>
          {getSelectedProductsCount() > 0 && (
            <button 
              className="add-to-interest-button"
              onClick={addAllToInterestList}
            >
              <Heart size={20} />
              Adicionar à Lista de Interesse
            </button>
          )}
        </div>

        <div className="category-filter">
          {categories.map(category => (
            <button
              key={category.id}
              className={`category-button ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>

        <div className="products-grid">
          {filteredProducts.map(product => (
            <div key={product.id} className="product-card">
              <div className="product-image">
                <img 
                  src={require(`../assets/${product.image}`)} 
                  alt={product.name}
                  onError={(e) => {
                    e.target.src = require('../assets/logo.png');
                  }}
                />
              </div>
              <div className="product-info">
                <h3>{product.name}</h3>
                <p className="product-description">{product.description}</p>
                <div className="quantity-controls">
                  <button
                    className="quantity-btn"
                    onClick={() => handleProductQuantityChange(
                      product.id, 
                      (selectedProducts[product.id] || 0) - 1
                    )}
                  >
                    <Minus size={16} />
                  </button>
                  <span className="quantity">{selectedProducts[product.id] || 0}</span>
                  <button
                    className="quantity-btn"
                    onClick={() => handleProductQuantityChange(
                      product.id, 
                      (selectedProducts[product.id] || 0) + 1
                    )}
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {getSelectedProductsCount() > 0 && (
          <div className="customer-info-section">
            <h2>Informações de Contacto</h2>
            
            <form onSubmit={handleSubmit} className="customer-form">
              {submitStatus && (
                <div className={`status-message ${submitStatus.type}`}>
                  {submitStatus.message}
                </div>
              )}
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Nome Completo *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={customerInfo.name}
                    onChange={handleCustomerInfoChange}
                    required
                    placeholder="O seu nome completo"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={customerInfo.email}
                    onChange={handleCustomerInfoChange}
                    required
                    placeholder="seuemail@exemplo.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Telefone *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={customerInfo.phone}
                    onChange={handleCustomerInfoChange}
                    required
                    placeholder="+351 256 418 202"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="company">Empresa (opcional)</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={customerInfo.company}
                    onChange={handleCustomerInfoChange}
                    placeholder="Nome da empresa"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address">Morada de Instalação *</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={customerInfo.address}
                  onChange={handleCustomerInfoChange}
                  required
                  placeholder="Morada completa onde será feita a instalação"
                />
              </div>

              <div className="form-group">
                <label htmlFor="notes">Observações (opcional)</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={customerInfo.notes}
                  onChange={handleCustomerInfoChange}
                  rows={4}
                  placeholder="Informações adicionais sobre o projeto, instalação, prazos, etc."
                />
              </div>

              <div className="quote-final-summary">
                <h3>Resumo do Orçamento</h3>
                <div className="summary-details">
                  <div className="summary-line">
                    <span>Produtos selecionados</span>
                    <span>{getSelectedProductsCount()} itens</span>
                  </div>
                  <div className="summary-line">
                    <span>Instalação</span>
                    <span>A consultar</span>
                  </div>
                  <div className="summary-line total">
                    <span>Preços</span>
                    <span>A consultar</span>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>Enviando...</>
                  ) : (
                    <>
                      <Calculator size={20} />
                      Solicitar Orçamento
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdvancedQuote;
